<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue';
import { useConfirm } from 'primevue/useconfirm';
import { useDialog } from 'primevue/usedialog';
import Button from 'primevue/button';
import Column from 'primevue/column';
import ConfirmPopup from 'primevue/confirmpopup';
import DataTable from 'primevue/datatable';
import DynamicDialog from 'primevue/dynamicdialog';
import FileUpload, { type FileUploadUploaderEvent } from 'primevue/fileupload';
import Message from 'primevue/message';
import Paginator from 'primevue/paginator';
import ProgressSpinner from 'primevue/progressspinner';
import Skeleton from 'primevue/skeleton';
import Tab from 'primevue/tab';
import TabList from 'primevue/tablist';
import TabPanel from 'primevue/tabpanel';
import TabPanels from 'primevue/tabpanels';
import Tabs from 'primevue/tabs';
import Tag from 'primevue/tag';
import Toast from 'primevue/toast';
import DigitalVendorEndpointForm from './DigitalVendorEndpointForm.vue';
import { useDateFns, useDigitalVendorEndpoint, useDigitalVendorHealth, useToastNotifications } from '@apparatix/composables';
import { DigitalVendorEndpointStore as store } from '../stores/DigitalVendorEndpointStore';
import type {
  DigitalVendor,
  DigitalVendorBoardPage,
  DigitalVendorEndpoint,
  DigitalVendorHealth,
  DigitalVendorOperationalStatus,
  DigitalVendorHealthStatus,
  DigitalVendorHealthWarning,
  PrimevueSeverity,
} from '@apparatix/types';

interface Props {
  sspList: DigitalVendor[];
  endpointTypes: Array<{ name: string, value: string }>;
  healthUrl: string;
  unregisteredBoardUrls: Record<string, string>;
}

const props = defineProps<Props>();

store.setVendors(props.sspList);
store.setAvailableEndpointTypes(props.endpointTypes);

const { deleteEndpoint, uploadFile } = useDigitalVendorEndpoint();
const {
  health, isLoading, didError, loadHealth, loadUnregisteredBoards,
} = useDigitalVendorHealth(props.healthUrl, props.unregisteredBoardUrls);
const { formatDate, formatDistance } = useDateFns();
const confirmDelete = useConfirm();
const editDialog = useDialog();
const { showSuccess, showError } = useToastNotifications();
const activeVendorCode = ref('Vistar');
const boardPage = ref<DigitalVendorBoardPage | null>(null);
const boardVendorCode = ref<string | null>(null);
const boardsLoading = ref(false);
const numberFormatter = new Intl.NumberFormat();
const novaAdsBaseUrl = 'https://dymen.screenverse.io/api/v1';
const enabledVendors = computed(() => health.value?.vendors.filter(vendor => vendor.operationalStatus === 'enabled') ?? []);

onMounted(loadHealth);

const showNewOrEditModal = (vendor: DigitalVendor, endpoint: DigitalVendorEndpoint | null = null) => {
  if (!vendor) return;

  store.setActiveEndpoint(endpoint ?? store.getEmptyEndpointModel(vendor.id));
  editDialog.open(DigitalVendorEndpointForm, {
    props: {
      header: endpoint === null ? 'Create new endpoint' : 'Edit endpoint',
      style: { width: 'min(720px, 90vw)' },
      modal: true,
      blockScroll: true,
      position: 'top',
    },
  });
};

const getTypeTagColor = (type: string): PrimevueSeverity | undefined => {
  switch (type.toLowerCase()) {
    case 'authorization': return undefined;
    case 'ads': return 'success';
    case 'inventory': return 'info';
    case 'reporting': return 'warn';
    default: return 'info';
  }
};

const statusSeverity = (status: DigitalVendorHealthStatus): PrimevueSeverity => ({
  healthy: 'success',
  warning: 'warn',
  error: 'danger',
  unknown: 'secondary',
}[status] as PrimevueSeverity);

const warningSeverity = (warning: DigitalVendorHealthWarning): PrimevueSeverity => {
  if (warning.code === 'SSP_NOT_CONFIGURED') return 'secondary';

  return ({
    info: 'info',
    warning: 'warn',
    error: 'danger',
  }[warning.severity] as PrimevueSeverity);
};

const operationalStatusLabel = (status: DigitalVendorOperationalStatus) => {
  switch (status) {
    case 'enabled': return 'Enabled';
    case 'not_configured': return 'Not configured';
    case 'needs_attention': return 'Needs attention';
    default: return 'Unknown';
  }
};

const operationalStatusSeverity = (status: DigitalVendorOperationalStatus): PrimevueSeverity => {
  switch (status) {
    case 'enabled': return 'success';
    case 'not_configured': return 'secondary';
    case 'needs_attention': return 'warn';
    default: return 'secondary';
  }
};

const sectionStatusLabel = (applicable: boolean, status: DigitalVendorHealthStatus) => applicable ? status : 'Not applicable';
const vendorIndicatorClass = (vendor: DigitalVendorHealth) => {
  if (vendor.operationalStatus === 'not_configured') return 'bg-slate-400';
  if (vendor.operationalStatus === 'needs_attention') return 'bg-amber-500';

  return {
    healthy: 'bg-emerald-500',
    warning: 'bg-amber-500',
    error: 'bg-rose-500',
    unknown: 'bg-slate-400',
  }[vendor.healthStatus];
};

const issueEntityLabels = new Map<string, string>([
  ['contacts2', 'Marketplace Account'],
  ['contracts_p', 'Marketplace Posting'],
  ['inventory', 'Inventory'],
  ['local_hal_options', 'HAL configuration'],
  ['marketplace_configurations', 'Inventory CPM configuration'],
  ['marketplace_eula', 'Marketplace EULA'],
  ['telemetry', 'Ad-request telemetry'],
  ['system_logs', 'Ad-request error logs'],
]);

const issueVendorLabel = (warning: DigitalVendorHealthWarning) => health.value?.vendors
  .find(vendor => vendor.code === warning.vendorCode)?.displayName ?? warning.vendorCode ?? 'Marketplace';

const issueRecordLabel = (warning: DigitalVendorHealthWarning) => {
  const entityLabel = issueEntityLabels.get(warning.entityType) ?? warning.entityType;

  if (!warning.recordId) return entityLabel;

  if (warning.entityType === 'contacts2') return `${entityLabel} · Account ID ${warning.recordId}`;
  if (warning.entityType === 'contracts_p') return `${entityLabel} · Posting ID ${warning.recordId}`;

  return `${entityLabel} · ${warning.recordId}`;
};

const formatIssueValue = (value: string | null) => value?.trim() || 'Not set';

const formatCount = (value: number) => numberFormatter.format(value);
const formatCpm = (value: number | null) => value === null ? 'Not applicable' : `$${value.toFixed(2)}`;
const registrationPercent = (vendor: DigitalVendorHealth) => vendor.inventory.eligibleBoards === 0
  ? 0
  : Math.round((vendor.inventory.registeredBoards / vendor.inventory.eligibleBoards) * 100);
const isNovaEnabled = (vendor: DigitalVendorHealth) => vendor.configuration.endpoints.some(endpoint => (
  endpoint.type.toLowerCase() === 'ads'
  && endpoint.baseUrl.trim().replace(/\/+$/, '').toLowerCase() === novaAdsBaseUrl
));

const copyRemediation = async (warning: DigitalVendorHealthWarning) => {
  const details = [
    `SSP: ${issueVendorLabel(warning)}`,
    `Finding: ${warning.code}`,
    `Affected record: ${issueRecordLabel(warning)}`,
    `Issue: ${warning.message}`,
    `Current: ${formatIssueValue(warning.actualValue)}`,
    `Fix: ${warning.remediation}`,
  ];

  if (warning.expectedValue !== null) details.splice(-1, 0, `Expected: ${formatIssueValue(warning.expectedValue)}`);

  await navigator.clipboard.writeText(details.join('\n'));
  showSuccess('Finding details copied');
};

const viewSspDetails = async (vendorCode: string) => {
  activeVendorCode.value = vendorCode;
  await nextTick();
  document.getElementById('ssp-details')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const fetchBoards = async (vendorCode: string, page = 1, perPage = 25) => {
  boardsLoading.value = true;
  boardVendorCode.value = vendorCode;
  boardPage.value = await loadUnregisteredBoards(vendorCode, page, perPage);
  boardsLoading.value = false;

  if (!boardPage.value) showError('Unable to load unregistered boards');
};

const onBoardPage = (event: { page: number, rows: number }) => {
  if (boardVendorCode.value) fetchBoards(boardVendorCode.value, event.page + 1, event.rows);
};

const canDelete = (type: string) => type.toLowerCase() === 'reporting';
const getDeleteButtonTooltipText = (type: string) => canDelete(type)
  ? 'Endpoints of type reporting cannot be deleted'
  : 'Delete this endpoint';

const onDeleteClick = (event: MouseEvent, endpoint: DigitalVendorEndpoint) => {
  confirmDelete.require({
    target: event.currentTarget as HTMLElement,
    message: 'Are you sure you want to delete this endpoint? This cannot be undone',
    header: 'Confirm delete',
    accept: () => handleDelete(endpoint),
    position: 'bottomleft',
    icon: 'pi pi-exclamation-triangle',
  });
};

const handleDelete = async (endpoint: DigitalVendorEndpoint) => {
  endpoint.isLoading = true;

  if (await deleteEndpoint(endpoint.id)) {
    store.removeEndpointFromList(endpoint);
  } else {
    endpoint.isLoading = false;
    showError('Error deleting endpoint');
  }
};

const onUpload = async (vendorId: number, event: FileUploadUploaderEvent) => {
  if (await uploadFile(vendorId, event.files[0])) {
    showSuccess('File successfully uploaded');
  } else {
    showError('Error uploading file');
  }
};
</script>

<template>
  <div class="min-h-screen bg-[var(--neutral-background)] px-4 py-6 sm:px-6 lg:px-8">
    <main class="mx-auto flex max-w-[1600px] flex-col gap-8">
      <section class="overflow-hidden rounded-xl bg-white shadow-sm ring-1 ring-slate-200">
        <div class="border-b border-teal-100 bg-gradient-to-r from-teal-50 via-white to-fuchsia-50 px-6 py-6">
          <div class="flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
            <div>
              <div class="mb-2 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-teal-700">
                <i class="pi pi-chart-bar" /> Marketplace operations
              </div>
              <h1 class="text-3xl font-light text-slate-900">
                Digital Vendor Health
              </h1>
              <p class="mt-2 max-w-3xl text-sm text-slate-600">
                Read-only readiness, registration, and traffic diagnostics for the five Marketplace SSPs.
              </p>
            </div>
            <div v-if="health" class="flex items-center gap-3">
              <div class="text-right text-xs text-slate-500">
                <div>Last evaluated</div>
                <div class="font-medium text-slate-700">
                  {{ formatDistance(health.generatedAt) }}
                </div>
              </div>
              <Tag :severity="statusSeverity(health.overallStatus)" :value="health.overallStatus.toUpperCase()" rounded />
              <Button
                icon="pi pi-refresh"
                label="Refresh"
                outlined
                size="small"
                @click="loadHealth"
              />
            </div>
          </div>
        </div>

        <div v-if="isLoading" class="space-y-5 p-6">
          <div class="grid gap-4 md:grid-cols-3">
            <Skeleton
              v-for="index in 3"
              :key="index"
              height="8rem"
              border-radius="12px"
            />
          </div>
          <Skeleton height="18rem" border-radius="12px" />
        </div>

        <div v-else-if="didError" class="p-6">
          <Message severity="error" :closable="false">
            The health dashboard could not be loaded. Endpoint administration below remains available.
          </Message>
          <Button
            class="mt-4"
            icon="pi pi-refresh"
            label="Try again"
            @click="loadHealth"
          />
        </div>

        <div v-else-if="health" class="space-y-8 p-6">
          <div class="grid gap-4 lg:grid-cols-[1.4fr_1fr_1fr]">
            <article class="rounded-xl border border-teal-200 bg-teal-50/70 p-5">
              <div class="flex items-start justify-between gap-4">
                <div>
                  <div class="text-xs font-semibold uppercase tracking-wider text-teal-700">
                    Marketplace EULA
                  </div>
                  <h2 class="mt-2 text-xl font-semibold text-slate-900">
                    {{ health.marketplaceEula.signed ? 'Signed and available' : 'Marketplace is not ready' }}
                  </h2>
                </div>
                <Tag :severity="statusSeverity(health.marketplaceEula.healthStatus)" :value="health.marketplaceEula.healthStatus" rounded />
              </div>
              <dl class="mt-5 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <dt class="text-slate-500">
                    Signed by
                  </dt><dd class="mt-1 font-medium text-slate-800">
                    {{ health.marketplaceEula.signerName ?? '—' }}
                  </dd>
                </div>
                <div>
                  <dt class="text-slate-500">
                    Accepted
                  </dt><dd class="mt-1 font-medium text-slate-800">
                    {{ health.marketplaceEula.signedAt ? formatDate(health.marketplaceEula.signedAt) : '—' }}
                  </dd>
                </div>
              </dl>
            </article>

            <article class="rounded-xl border border-rose-200 bg-rose-50/70 p-5">
              <div class="text-xs font-semibold uppercase tracking-wider text-rose-700">
                Needs attention
              </div>
              <div class="mt-4 flex items-end gap-3">
                <span class="text-4xl font-light text-rose-700">{{ health.issueTotals.error }}</span><span class="pb-1 text-sm text-slate-600">errors</span>
              </div>
              <div class="mt-3 text-sm text-slate-600">
                {{ health.issueTotals.warning }} warnings across Marketplace setup and SSP operations.
              </div>
            </article>

            <article class="rounded-xl border border-fuchsia-200 bg-fuchsia-50/70 p-5">
              <div class="text-xs font-semibold uppercase tracking-wider text-fuchsia-700">
                SSP health
              </div>
              <div class="mt-4 text-4xl font-light text-fuchsia-700">
                {{ enabledVendors.filter(vendor => vendor.healthStatus === 'healthy').length }} / {{ enabledVendors.length }}
              </div>
              <div class="mt-3 text-sm text-slate-600">
                enabled vendors currently clear every required dashboard check.
              </div>
              <div class="mt-2 text-xs text-slate-500">
                {{ health.vendors.filter(vendor => vendor.operationalStatus === 'not_configured').length }} not configured · {{ health.vendors.filter(vendor => vendor.operationalStatus === 'needs_attention').length }} need attention
              </div>
            </article>
          </div>

          <section class="overflow-hidden rounded-xl border border-indigo-200 bg-indigo-50/40">
            <div class="flex flex-col justify-between gap-2 border-b border-indigo-100 px-5 py-4 sm:flex-row sm:items-center">
              <div>
                <h2 class="flex items-center gap-2 text-lg font-semibold text-slate-900">
                  <i class="pi pi-play-circle text-indigo-600" /> Proof of play today
                </h2><p class="mt-1 text-xs text-slate-500">
                  Marketplace account PoP totals and eligible boards with activity.
                </p>
              </div><span class="text-xs text-slate-500">Today in the tenant timezone</span>
            </div>
            <div class="grid gap-px bg-indigo-100 sm:grid-cols-2 xl:grid-cols-5">
              <article v-for="vendor in health.vendors" :key="`pop-overview-${vendor.code}`" class="bg-white/70 px-5 py-4">
                <div class="text-sm font-semibold text-slate-800">
                  {{ vendor.displayName }}
                </div>
                <template v-if="vendor.proofOfPlay.available">
                  <div class="mt-3 text-2xl font-light text-indigo-700">
                    {{ formatCount(vendor.proofOfPlay.playsToday) }}
                  </div><div class="text-xs text-slate-500">
                    plays
                  </div><div class="mt-3 text-xs font-medium text-slate-700">
                    {{ formatCount(vendor.proofOfPlay.boardsWithPopToday) }}<template v-if="vendor.inventory.healthStatus !== 'unknown'">
                      / {{ formatCount(vendor.inventory.eligibleBoards) }}
                    </template> boards
                  </div>
                </template>
                <div v-else class="mt-3 text-sm font-medium text-slate-500">
                  PoP unavailable
                </div>
              </article>
            </div>
          </section>

          <section class="overflow-hidden rounded-xl border border-amber-200 bg-amber-50/40">
            <div class="flex flex-col justify-between gap-2 border-b border-amber-100 px-5 py-4 sm:flex-row sm:items-center">
              <div>
                <h2 class="flex items-center gap-2 text-lg font-semibold text-slate-900">
                  <i class="pi pi-exclamation-triangle text-amber-600" /> Ad-request error logs
                </h2><p class="mt-1 text-xs text-slate-500">
                  Potential SSP request failures recorded by the Digital Vendor ad-request process.
                </p>
              </div><span class="text-xs text-slate-500">Warning above {{ health.vendors[0]?.adRequestErrors.warningThreshold ?? 100 }} errors in a rolling 24 hours</span>
            </div>
            <div class="grid gap-px bg-amber-100 sm:grid-cols-2 xl:grid-cols-5">
              <article v-for="vendor in health.vendors" :key="`ad-request-errors-${vendor.code}`" class="bg-white/80 px-5 py-4">
                <div class="flex items-start justify-between gap-2">
                  <div class="text-sm font-semibold text-slate-800">
                    {{ vendor.displayName }}
                  </div><Tag :severity="vendor.adRequestErrors.applicable ? statusSeverity(vendor.adRequestErrors.healthStatus) : 'secondary'" :value="sectionStatusLabel(vendor.adRequestErrors.applicable, vendor.adRequestErrors.healthStatus)" rounded />
                </div>
                <div v-if="vendor.adRequestErrors.available" class="mt-4 grid grid-cols-3 gap-2 text-center">
                  <div class="rounded-md bg-slate-50 px-2 py-3">
                    <div class="text-lg font-medium text-slate-900">
                      {{ formatCount(vendor.adRequestErrors.errorsToday) }}
                    </div><div class="text-xs text-slate-500">
                      Today
                    </div>
                  </div>
                  <div class="rounded-md bg-slate-50 px-2 py-3">
                    <div class="text-lg font-medium text-slate-900">
                      {{ formatCount(vendor.adRequestErrors.errorsYesterday) }}
                    </div><div class="text-xs text-slate-500">
                      Yesterday
                    </div>
                  </div>
                  <div class="rounded-md px-2 py-3" :class="vendor.adRequestErrors.errorsLast24Hours > vendor.adRequestErrors.warningThreshold ? 'bg-amber-100' : 'bg-slate-50'">
                    <div class="text-lg font-medium" :class="vendor.adRequestErrors.errorsLast24Hours > vendor.adRequestErrors.warningThreshold ? 'text-amber-800' : 'text-slate-900'">
                      {{ formatCount(vendor.adRequestErrors.errorsLast24Hours) }}
                    </div><div class="text-xs text-slate-500">
                      Last 24h
                    </div>
                  </div>
                </div>
                <div v-else class="mt-4 text-sm font-medium text-slate-500">
                  Error-log summary unavailable
                </div>
                <div class="mt-3 break-words text-xs text-slate-500">
                  {{ vendor.adRequestErrors.message }}
                </div>
                <div v-if="vendor.adRequestErrors.latestErrorAt" class="mt-2 text-xs text-slate-500">
                  Latest {{ formatDistance(vendor.adRequestErrors.latestErrorAt) }}
                </div>
              </article>
            </div>
          </section>

          <section>
            <div class="mb-3 flex items-center justify-between">
              <h2 class="text-lg font-semibold text-slate-900">
                Marketplace readiness chain
              </h2><span class="text-xs text-slate-500">Top-down dependency order</span>
            </div>
            <div class="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
              <article v-for="(stage, index) in health.stages" :key="stage.key" class="relative rounded-lg border border-slate-200 bg-white p-4">
                <div class="mb-3 flex items-center justify-between">
                  <span class="flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600">{{ index + 1 }}</span><Tag :severity="statusSeverity(stage.status)" :value="stage.status" rounded />
                </div>
                <div class="text-sm font-semibold text-slate-800">
                  {{ stage.label }}
                </div>
                <div class="mt-1 text-xs text-slate-500">
                  {{ stage.issueCount }} findings
                </div>
              </article>
            </div>
          </section>

          <section v-if="health.topIssues.length">
            <h2 class="mb-3 text-lg font-semibold text-slate-900">
              Highest-priority findings
            </h2>
            <div class="divide-y divide-slate-100 rounded-lg border border-slate-200">
              <article v-for="issue in health.topIssues" :key="`${issue.code}-${issue.vendorCode}-${issue.recordId}`" class="flex flex-col gap-3 p-4 lg:flex-row lg:items-start">
                <Tag
                  :severity="warningSeverity(issue)"
                  :value="issue.severity"
                  class="self-start"
                  rounded
                />
                <div class="min-w-0 flex-1">
                  <div class="flex flex-wrap items-center gap-2 text-xs">
                    <span class="rounded-full bg-indigo-50 px-2.5 py-1 font-semibold text-indigo-700">{{ issueVendorLabel(issue) }}</span>
                    <span class="rounded-full bg-slate-100 px-2.5 py-1 font-medium text-slate-600">{{ issueRecordLabel(issue) }}</span>
                    <code class="text-slate-500">{{ issue.code }}</code>
                  </div><p class="mt-2 font-medium text-slate-900">
                    {{ issue.message }}
                  </p>
                  <dl class="mt-3 grid gap-2 text-xs sm:grid-cols-2">
                    <div class="rounded-md bg-rose-50 px-3 py-2">
                      <dt class="font-semibold uppercase tracking-wide text-rose-700">
                        Current
                      </dt><dd class="mt-1 break-words text-slate-700">
                        {{ formatIssueValue(issue.actualValue) }}
                      </dd>
                    </div>
                    <div v-if="issue.expectedValue !== null" class="rounded-md bg-emerald-50 px-3 py-2">
                      <dt class="font-semibold uppercase tracking-wide text-emerald-700">
                        Expected
                      </dt><dd class="mt-1 break-words text-slate-700">
                        {{ formatIssueValue(issue.expectedValue) }}
                      </dd>
                    </div>
                  </dl>
                  <p class="mt-3 text-sm text-slate-600">
                    <span class="font-semibold text-slate-700">Fix:</span> {{ issue.remediation }}
                  </p>
                </div>
                <div class="flex shrink-0 flex-wrap gap-1 lg:flex-col">
                  <Button
                    v-if="issue.vendorCode"
                    icon="pi pi-eye"
                    label="View SSP"
                    text
                    size="small"
                    @click="viewSspDetails(issue.vendorCode)"
                  />
                  <Button
                    icon="pi pi-copy"
                    label="Copy details"
                    text
                    size="small"
                    @click="copyRemediation(issue)"
                  />
                </div>
              </article>
            </div>
          </section>

          <section id="ssp-details" class="scroll-mt-4">
            <div class="mb-3">
              <h2 class="text-lg font-semibold text-slate-900">
                SSP details
              </h2><p class="mt-1 text-sm text-slate-500">
                Configuration is intentionally read-only here.
              </p>
            </div>
            <Tabs v-model:value="activeVendorCode">
              <TabList>
                <Tab v-for="vendor in health.vendors" :key="vendor.code" :value="vendor.code">
                  <span class="flex items-center gap-2">{{ vendor.displayName }}<span v-if="vendor.alias" class="text-xs text-slate-400">({{ vendor.alias }})</span><span class="h-2 w-2 rounded-full" :class="vendorIndicatorClass(vendor)" /></span>
                </Tab>
              </TabList>
              <TabPanels>
                <TabPanel v-for="vendor in health.vendors" :key="vendor.code" :value="vendor.code">
                  <div class="flex items-center gap-2 pt-3 text-xs text-slate-500">
                    Operational status
                    <Tag :severity="operationalStatusSeverity(vendor.operationalStatus)" :value="operationalStatusLabel(vendor.operationalStatus)" rounded />
                    <span v-if="vendor.operationalStatus === 'not_configured'">Downstream readiness checks are not applicable until this SSP is configured.</span>
                  </div>
                  <div class="mt-3 grid gap-5 xl:grid-cols-2">
                    <article class="rounded-xl border border-teal-200 bg-teal-50/40 p-5">
                      <div class="flex items-center justify-between">
                        <h3 class="font-semibold text-slate-900">
                          Marketplace Account
                        </h3><Tag :severity="statusSeverity(vendor.account.healthStatus)" :value="vendor.account.healthStatus" rounded />
                      </div>
                      <dl class="mt-5 grid gap-4 text-sm sm:grid-cols-2">
                        <div>
                          <dt class="text-slate-500">
                            Name
                          </dt><dd class="mt-1 font-medium text-slate-800">
                            {{ vendor.account.name ?? 'Missing' }}
                          </dd>
                        </div>
                        <div>
                          <dt class="text-slate-500">
                            Account ID
                          </dt><dd class="mt-1 font-medium text-slate-800">
                            {{ vendor.account.id ?? '—' }}
                          </dd>
                        </div>
                        <div>
                          <dt class="text-slate-500">
                            Status
                          </dt><dd class="mt-1 font-medium text-slate-800">
                            {{ vendor.account.accountStatus ?? '—' }}
                          </dd>
                        </div>
                        <div>
                          <dt class="text-slate-500">
                            Website
                          </dt><dd class="mt-1 break-all font-medium text-slate-800">
                            {{ vendor.account.websiteRequired ? (vendor.account.website || 'Missing') : 'Not used for this SSP' }}
                          </dd>
                        </div>
                        <div>
                          <dt class="text-slate-500">
                            Office
                          </dt><dd class="mt-1 font-medium text-slate-800">
                            {{ vendor.account.office ?? '—' }}
                          </dd>
                        </div>
                      </dl>
                    </article>

                    <article class="rounded-xl border border-fuchsia-200 bg-fuchsia-50/40 p-5">
                      <div class="flex items-center justify-between">
                        <h3 class="font-semibold text-slate-900">
                          Active Marketplace Postings
                        </h3><Tag :severity="statusSeverity(vendor.posting.healthStatus)" :value="vendor.posting.healthStatus" rounded />
                      </div>
                      <div class="mt-5 grid grid-cols-3 gap-4 text-sm">
                        <div>
                          <div class="text-slate-500">
                            Active Postings
                          </div><div class="mt-1 text-2xl font-light text-slate-900">
                            {{ formatCount(vendor.posting.activePostingCount) }}
                          </div>
                        </div><div>
                          <div class="text-slate-500">
                            Base CPM
                          </div><div class="mt-1 text-2xl font-light text-slate-900">
                            {{ formatCpm(vendor.posting.baseCpm) }}
                          </div>
                        </div><div>
                          <div class="text-slate-500">
                            Inventory CPMs
                          </div><div class="mt-1 text-2xl font-light text-slate-900">
                            {{ vendor.posting.perInventoryCpmCount }}
                          </div>
                        </div>
                      </div>
                      <p v-if="vendor.posting.nonActivePostingCount" class="mt-4 text-xs text-slate-500">
                        {{ formatCount(vendor.posting.nonActivePostingCount) }} non-active
                        {{ vendor.posting.nonActivePostingCount === 1 ? 'posting is' : 'postings are' }} excluded.
                      </p>
                      <p v-if="vendor.posting.postings.length > 30" class="mt-4 text-xs text-slate-500">
                        All {{ formatCount(vendor.posting.postings.length) }} Active postings are shown; scroll within the table after the first 30.
                      </p>
                      <div
                        v-if="vendor.posting.postings.length"
                        class="mt-3 overflow-auto overscroll-contain"
                        :class="{ 'max-h-[64rem]': vendor.posting.postings.length > 30 }"
                        role="region"
                        :aria-label="`${vendor.displayName} Active Marketplace postings`"
                        tabindex="0"
                      >
                        <table class="w-full text-left text-xs">
                          <thead class="sticky top-0 z-10 bg-fuchsia-50 text-slate-500 shadow-sm">
                            <tr>
                              <th class="pb-2">
                                Posting ID
                              </th><th class="pb-2">
                                Contract ID
                              </th>
                              <th class="pb-2">
                                Inventory
                              </th><th class="pb-2">
                                Class
                              </th><th class="pb-2">
                                Status
                              </th>
                            </tr>
                          </thead><tbody class="divide-y divide-fuchsia-100">
                            <tr v-for="posting in vendor.posting.postings" :key="posting.id">
                              <td class="py-2 font-medium">
                                {{ posting.id }}
                              </td><td class="py-2">
                                {{ posting.contractId }}
                              </td><td class="py-2 font-medium">
                                {{ posting.inventoryNumber }}
                              </td><td class="py-2">
                                {{ posting.class }}
                              </td><td class="py-2">
                                {{ posting.status }}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </article>

                    <article class="rounded-xl border border-slate-200 p-5 xl:col-span-2">
                      <div class="flex items-center justify-between">
                        <div class="flex flex-wrap items-center gap-2">
                          <h3 class="font-semibold text-slate-900">
                            HAL & API configuration
                          </h3>
                          <Tag
                            v-if="isNovaEnabled(vendor)"
                            icon="pi pi-sparkles"
                            severity="info"
                            value="Nova Enabled"
                            rounded
                          />
                        </div><Tag :severity="statusSeverity(vendor.configuration.healthStatus)" :value="vendor.configuration.healthStatus" rounded />
                      </div>
                      <div class="mt-5 grid gap-5 lg:grid-cols-3">
                        <dl class="space-y-3 text-sm">
                          <div>
                            <dt class="text-slate-500">
                              SSP prefix
                            </dt><dd class="mt-1 font-medium">
                              {{ vendor.configuration.sspPrefix ?? 'Missing' }}
                            </dd>
                          </div><div>
                            <dt class="text-slate-500">
                              Credentials
                            </dt><dd class="mt-1 font-medium">
                              {{ vendor.configuration.credentialsPresent ? 'All required values present' : 'Incomplete' }}
                            </dd>
                          </div><div v-if="vendor.configuration.identifierType">
                            <dt class="text-slate-500">
                              {{ vendor.configuration.identifierType }}
                            </dt><dd class="mt-1 font-medium">
                              {{ vendor.configuration.identifier ?? 'Missing' }}
                            </dd>
                          </div>
                        </dl>
                        <div class="lg:col-span-2">
                          <div class="mb-2 text-sm text-slate-500">
                            Configured API hosts
                          </div><div v-if="Object.keys(vendor.configuration.apiHosts.urls).length" class="space-y-2">
                            <div v-for="(url, label) in vendor.configuration.apiHosts.urls" :key="label" class="grid gap-1 rounded-md bg-slate-50 px-3 py-2 text-xs sm:grid-cols-[160px_1fr]">
                              <span class="font-medium text-slate-600">{{ label }}</span><span class="break-all text-slate-800">{{ url }}</span>
                            </div>
                          </div><div v-else class="text-sm text-rose-600">
                            No safe host values found.
                          </div>
                        </div>
                      </div>
                    </article>

                    <article class="rounded-xl border border-slate-200 p-5">
                      <div class="flex items-center justify-between">
                        <h3 class="font-semibold text-slate-900">
                          Inventory registration
                        </h3><Tag :severity="vendor.inventory.applicable ? statusSeverity(vendor.inventory.healthStatus) : 'secondary'" :value="sectionStatusLabel(vendor.inventory.applicable, vendor.inventory.healthStatus)" rounded />
                      </div>
                      <div class="mt-5 flex items-end justify-between">
                        <div>
                          <div class="text-3xl font-light text-slate-900">
                            {{ formatCount(vendor.inventory.registeredBoards) }} / {{ formatCount(vendor.inventory.eligibleBoards) }}
                          </div><div class="mt-1 text-sm text-slate-500">
                            eligible boards registered
                          </div>
                        </div><div class="text-2xl font-light text-teal-700">
                          {{ registrationPercent(vendor) }}%
                        </div>
                      </div>
                      <div class="mt-4 h-2 overflow-hidden rounded-full bg-slate-100">
                        <div class="h-full rounded-full bg-teal-500" :style="{ width: `${registrationPercent(vendor)}%` }" />
                      </div>
                      <div class="mt-4 grid grid-cols-3 gap-2 text-center text-xs">
                        <div class="rounded-md bg-slate-50 p-2">
                          <div class="text-lg font-medium">
                            {{ vendor.inventory.unregisteredBoards }}
                          </div><div class="text-slate-500">
                            unregistered
                          </div>
                        </div><div class="rounded-md bg-slate-50 p-2">
                          <div class="text-lg font-medium">
                            {{ vendor.inventory.mappedBoards }}
                          </div><div class="text-slate-500">
                            mapped
                          </div>
                        </div><div class="rounded-md bg-slate-50 p-2">
                          <div class="text-lg font-medium">
                            {{ vendor.inventory.invalidRegistrationJson }}
                          </div><div class="text-slate-500">
                            invalid JSON
                          </div>
                        </div>
                      </div>
                      <Button
                        v-if="vendor.inventory.applicable && vendor.inventory.unregisteredBoards"
                        class="mt-4"
                        icon="pi pi-list"
                        label="Review unregistered boards"
                        outlined
                        size="small"
                        @click="fetchBoards(vendor.code)"
                      />
                    </article>

                    <article class="rounded-xl border border-slate-200 p-5">
                      <div class="flex items-center justify-between">
                        <h3 class="font-semibold text-slate-900">
                          Ad-request traffic
                        </h3><Tag :severity="vendor.telemetry.applicable ? statusSeverity(vendor.telemetry.healthStatus) : 'secondary'" :value="sectionStatusLabel(vendor.telemetry.applicable, vendor.telemetry.healthStatus)" rounded />
                      </div>
                      <div class="mt-5 grid grid-cols-3 gap-3">
                        <div v-for="window in [{ label: 'Last hour', boards: vendor.telemetry.requestingBoardsLastHour, requests: vendor.telemetry.requestsLastHour }, { label: 'Today', boards: vendor.telemetry.requestingBoardsToday, requests: vendor.telemetry.requestsToday }, { label: 'Last 7 days', boards: vendor.telemetry.requestingBoardsLast7Days, requests: vendor.telemetry.requestsLast7Days }]" :key="window.label" class="rounded-lg bg-slate-50 p-3">
                          <div class="text-xs font-medium text-slate-500">
                            {{ window.label }}
                          </div><div class="mt-2 text-xl font-light">
                            {{ formatCount(window.boards) }}
                          </div><div class="text-xs text-slate-500">
                            boards
                          </div><div class="mt-2 text-sm font-medium">
                            {{ formatCount(window.requests) }}
                          </div><div class="text-xs text-slate-500">
                            requests
                          </div>
                        </div>
                      </div>
                      <div class="mt-4 text-xs text-slate-500">
                        {{ formatCount(vendor.telemetry.retainedRequests) }} requests in retained history<span v-if="vendor.telemetry.latestRequest"> · latest {{ formatDistance(vendor.telemetry.latestRequest) }}</span>
                      </div>
                    </article>

                    <article class="rounded-xl border border-indigo-200 bg-indigo-50/30 p-5 xl:col-span-2">
                      <div>
                        <h3 class="flex items-center gap-2 font-semibold text-slate-900">
                          <i class="pi pi-play-circle text-indigo-600" /> Proof of play
                        </h3><p class="mt-1 text-xs text-slate-500">
                          PoP mapped through {{ vendor.account.name ?? 'the Marketplace account' }}<template v-if="vendor.account.id">
                            (Account ID {{ vendor.account.id }})
                          </template>.
                        </p>
                      </div>
                      <Message
                        v-if="!vendor.proofOfPlay.available"
                        class="mt-4"
                        severity="warn"
                        :closable="false"
                      >
                        Proof-of-play totals could not be loaded for this SSP.
                      </Message>
                      <div v-else class="mt-5 grid gap-4 md:grid-cols-3">
                        <div v-for="period in [{ label: 'Last hour', plays: vendor.proofOfPlay.playsLastHour, boards: vendor.proofOfPlay.boardsWithPopLastHour }, { label: 'Today', plays: vendor.proofOfPlay.playsToday, boards: vendor.proofOfPlay.boardsWithPopToday }, { label: 'Last 7 days', plays: vendor.proofOfPlay.playsLast7Days, boards: vendor.proofOfPlay.boardsWithPopLast7Days }]" :key="period.label" class="rounded-lg border border-indigo-100 bg-white p-4">
                          <div class="text-xs font-semibold uppercase tracking-wider text-indigo-700">
                            {{ period.label }}
                          </div><div class="mt-3 flex flex-wrap items-end justify-between gap-3">
                            <div>
                              <div class="text-3xl font-light text-slate-900">
                                {{ formatCount(period.plays) }}
                              </div><div class="text-xs text-slate-500">
                                plays
                              </div>
                            </div>
                            <div class="text-right">
                              <div class="text-xl font-medium text-teal-700">
                                {{ formatCount(period.boards) }}<template v-if="vendor.inventory.healthStatus !== 'unknown'">
                                  / {{ formatCount(vendor.inventory.eligibleBoards) }}
                                </template>
                              </div><div class="text-xs text-slate-500">
                                boards with PoP<template v-if="vendor.inventory.healthStatus !== 'unknown'">
                                  / eligible
                                </template>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </article>

                    <article v-if="vendor.issues.length" class="rounded-xl border border-amber-200 bg-amber-50/40 p-5 xl:col-span-2">
                      <h3 class="font-semibold text-slate-900">
                        All {{ vendor.displayName }} findings
                      </h3>
                      <p v-if="vendor.issues.length > 10" class="mt-2 text-xs text-slate-500">
                        All {{ formatCount(vendor.issues.length) }} findings are shown; scroll within this area after roughly 10.
                      </p>
                      <div
                        class="mt-3 divide-y divide-slate-100 overflow-hidden rounded-lg border border-slate-200 bg-white"
                        :class="{ 'max-h-[72rem] overflow-y-auto overscroll-contain': vendor.issues.length > 10 }"
                        role="region"
                        :aria-label="`All ${vendor.displayName} findings`"
                        tabindex="0"
                      >
                        <article v-for="issue in vendor.issues" :key="`${issue.code}-${issue.recordId}`" class="flex flex-col gap-3 p-4 lg:flex-row lg:items-start">
                          <Tag
                            :severity="warningSeverity(issue)"
                            :value="issue.severity"
                            class="self-start"
                            rounded
                          />
                          <div class="min-w-0 flex-1">
                            <div class="flex flex-wrap items-center gap-2 text-xs">
                              <span class="rounded-full bg-indigo-50 px-2.5 py-1 font-semibold text-indigo-700">{{ issueVendorLabel(issue) }}</span>
                              <span class="rounded-full bg-slate-100 px-2.5 py-1 font-medium text-slate-600">{{ issueRecordLabel(issue) }}</span>
                              <code class="text-slate-500">{{ issue.code }}</code>
                            </div><p class="mt-2 font-medium text-slate-900">
                              {{ issue.message }}
                            </p>
                            <dl class="mt-3 grid gap-2 text-xs sm:grid-cols-2">
                              <div class="rounded-md bg-rose-50 px-3 py-2">
                                <dt class="font-semibold uppercase tracking-wide text-rose-700">
                                  Current
                                </dt><dd class="mt-1 break-words text-slate-700">
                                  {{ formatIssueValue(issue.actualValue) }}
                                </dd>
                              </div>
                              <div v-if="issue.expectedValue !== null" class="rounded-md bg-emerald-50 px-3 py-2">
                                <dt class="font-semibold uppercase tracking-wide text-emerald-700">
                                  Expected
                                </dt><dd class="mt-1 break-words text-slate-700">
                                  {{ formatIssueValue(issue.expectedValue) }}
                                </dd>
                              </div>
                            </dl>
                            <p class="mt-3 text-sm text-slate-600">
                              <span class="font-semibold text-slate-700">Fix:</span> {{ issue.remediation }}
                            </p>
                          </div>
                          <Button
                            icon="pi pi-copy"
                            label="Copy details"
                            text
                            size="small"
                            @click="copyRemediation(issue)"
                          />
                        </article>
                      </div>
                    </article>
                  </div>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </section>

          <section v-if="boardVendorCode && (boardsLoading || boardPage)" class="rounded-xl border border-slate-200 p-5">
            <div class="mb-4 flex items-center justify-between">
              <div>
                <h3 class="font-semibold text-slate-900">
                  {{ boardVendorCode }} unregistered boards
                </h3><p class="mt-1 text-xs text-slate-500">
                  Read-only list of registration-ready, non-excluded digital inventory.
                </p>
              </div><Button
                icon="pi pi-times"
                text
                rounded
                aria-label="Close board list"
                @click="boardVendorCode = null; boardPage = null"
              />
            </div>
            <ProgressSpinner
              v-if="boardsLoading"
              class="mx-auto block"
              style="width: 40px; height: 40px"
              stroke-width="3"
            />
            <template v-else-if="boardPage">
              <DataTable :value="boardPage.data" striped-rows size="small">
                <Column field="inventoryNumber" header="Inventory" /><Column header="Dimensions">
                  <template #body="{ data }">
                    {{ data.pixelWidth }} × {{ data.pixelHeight }}
                  </template>
                </Column><Column header="Inventory mapping">
                  <template #body="{ data }">
                    <Tag :severity="data.mappingPresent ? 'success' : 'warn'" :value="data.mappingPresent ? 'Present' : 'Missing'" />
                  </template>
                </Column><Column field="registrationJsonStatus" header="Registration JSON" />
              </DataTable>
              <Paginator
                :first="(boardPage.meta.currentPage - 1) * boardPage.meta.perPage"
                :rows="boardPage.meta.perPage"
                :total-records="boardPage.meta.total"
                :rows-per-page-options="[25, 50, 100]"
                @page="onBoardPage"
              />
            </template>
          </section>
        </div>
      </section>

      <section class="rounded-xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
        <div class="mb-6">
          <div class="text-xs font-semibold uppercase tracking-wider text-indigo-600">
            Administration
          </div><h2 class="mt-1 text-2xl font-light text-slate-900">
            Digital Vendor Endpoints
          </h2><p class="mt-1 text-sm text-slate-500">
            Existing endpoint editing and backfill tools.
          </p>
        </div>
        <div class="space-y-8">
          <DataTable
            v-for="vendor in store.vendors"
            :key="vendor.display_name"
            striped-rows
            :value="vendor.digital_vendor_endpoints"
            class="p-datatable-sm"
            :pt="{ header: () => ({ style: { 'background-color': '#E8EAFF' } }), table: () => ({ style: { 'table-layout': 'fixed' } }) }"
          >
            <template #header>
              <div class="flex flex-wrap items-center gap-2">
                <h3 class="min-w-[220px] grow text-2xl font-light">
                  {{ vendor.display_name }}
                </h3><FileUpload
                  v-tooltip.left="'Upload backfill'"
                  name="file"
                  url="/digital-vendor/admin/import"
                  mode="basic"
                  accept=".csv"
                  choose-label="Backfill"
                  :auto="true"
                  custom-upload
                  @uploader="onUpload(vendor.id, $event)"
                /><Button
                  v-tooltip.left="`Add a new Digital Vendor Endpoint to ${vendor.display_name}`"
                  label="Add"
                  icon="pi pi-plus"
                  size="small"
                  @click="showNewOrEditModal(vendor)"
                />
              </div>
            </template>
            <Column header="Type" style="width: 130px">
              <template #body="{ data }">
                <Tag :severity="getTypeTagColor(data.type)" :value="data.type" />
              </template>
            </Column>
            <Column header="Base URL">
              <template #body="{ data }">
                <a
                  :href="data.base_url"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="break-all hover:underline"
                >{{ data.base_url }}</a>
              </template>
            </Column>
            <Column header="Created" style="width: 150px">
              <template #body="{ data }">
                {{ formatDate(data.created_at) }}
              </template>
            </Column>
            <Column header="Updated" style="width: 150px">
              <template #body="{ data }">
                {{ formatDistance(data.updated_at) }}
              </template>
            </Column>
            <Column style="width: 70px">
              <template #body="{ data }">
                <Button
                  v-tooltip.left="'Edit this endpoint'"
                  size="small"
                  severity="info"
                  rounded
                  outlined
                  icon="pi pi-file-edit"
                  @click="showNewOrEditModal(vendor, data)"
                />
              </template>
            </Column>
            <Column style="width: 70px">
              <template #body="{ data }">
                <div v-tooltip.left="getDeleteButtonTooltipText(data.type)">
                  <Button
                    v-if="!data.isLoading"
                    size="small"
                    severity="danger"
                    :disabled="canDelete(data.type)"
                    rounded
                    outlined
                    icon="pi pi-trash"
                    @click="onDeleteClick($event, data)"
                  /><ProgressSpinner
                    v-else
                    style="width: 40px; height: 40px"
                    stroke-width="2"
                    animation-duration=".5s"
                  />
                </div>
              </template>
            </Column>
          </DataTable>
        </div>
      </section>
    </main>

    <DynamicDialog />
    <ConfirmPopup />
    <Toast />
  </div>
</template>
